// QR Kiosk v2 — KV2-9 loading skeleton. Mirrors the real layout 1:1 (bug #13 discipline:
// a skeleton whose shape differs from the loaded page causes a visible reflow jump).
// Same order and the same 12px column gap as NameplateV2's 200 branch, which since owner
// ruling D15 (2026-08-14) is only TWO blocks: escalation row → plate (band, photo, rows).
// Nothing follows the plate — no in-service status line, no actions card, no ghost rows —
// so nothing follows it here either. No id-strip bar: the real plate no longer renders one
// (owner ruling 2026-08-13, see PlateCard).
// Every block height below is the real component's measured height — when a nameplate
// component's geometry changes, change it HERE in the same commit.
import React from "react";
import { SkeletonBlock } from "../shared/SkeletonBlocks";

/** Real PlateRow: 10px/16px padding around an 18px value line (+14px when a sub-line runs). */
function RowSk({ top, sub }: { top?: boolean; sub?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        borderTop: top ? "1px solid var(--border-soft)" : "none",
      }}
    >
      <SkeletonBlock w={104} h={9} r={4} />
      <span style={{ flex: 1 }} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        <SkeletonBlock w={112} h={12} r={4} />
        {sub && <SkeletonBlock w={84} h={8} r={4} />}
      </div>
    </div>
  );
}

export function NameplateSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      {/* Escalation row — caption + two 34px pills inside their 44px tap targets. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, height: 44 }}>
        <SkeletonBlock w={148} h={10} r={4} />
        <span style={{ flex: 1 }} />
        <SkeletonBlock w={78} h={34} r={999} />
        <SkeletonBlock w={99} h={34} r={999} />
      </div>

      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--tier-accent-line)",
          borderRadius: 12,
          boxShadow: "inset 0 0 0 3px var(--card), inset 0 0 0 4px var(--tier-accent-line), var(--shadow)",
          overflow: "hidden",
        }}
      >
        {/* Header band is a solid slate block in the real card — keep it solid here too so
            the heaviest element does not pop in on load. 12px padding + 12px line = 36px. */}
        <div style={{ height: 36, background: "var(--tier-accent)", opacity: 0.55 }} />
        <div style={{ height: 148, margin: "0 5px", borderBottom: "1px solid var(--border-soft)" }}>
          <SkeletonBlock w="100%" h="100%" r={0} />
        </div>
        <div style={{ padding: "0 5px 4px" }}>
          {/* Block A: 5 rows, the 2nd carries the type-name sub-line. */}
          {[0, 1, 2, 3, 4].map((i) => (
            <RowSk key={i} top={i > 0} sub={i === 1} />
          ))}
        </div>
        {/* Last block carries the plate's 12px closing pad — mirrors PlateCard's blockPad. */}
        <div style={{ borderTop: "1px solid var(--border)", padding: "0 5px 12px" }}>
          {[0, 1, 2].map((i) => (
            <RowSk key={i} top={i > 0} />
          ))}
        </div>
      </div>
      {/* The plate is the LAST element — D15. Anything added after this line puts the
          skeleton out of sync with the loaded page and reintroduces the reflow jump. */}
    </div>
  );
}

export default NameplateSkeleton;
